import { createContext, useContext, useState, useCallback } from 'react';
import api, { TOKEN_KEY, TOKEN_ADMIN_KEY, API_URL } from '../services/api';

const AuthContext = createContext(null);

function decodificar(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp * 1000 < Date.now()) return null;
    return payload;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    let token = localStorage.getItem(TOKEN_KEY);
    // Visualização ("Ver como") vencida enquanto a aba estava fechada: volta pro admin.
    if (token && !decodificar(token) && localStorage.getItem(TOKEN_ADMIN_KEY)) {
      token = localStorage.getItem(TOKEN_ADMIN_KEY);
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.removeItem(TOKEN_ADMIN_KEY);
    }
    if (!token) return null;
    const payload = decodificar(token);
    if (!payload) localStorage.removeItem(TOKEN_KEY);
    return payload;
  });

  const login = useCallback(async (username, senha) => {
    const { data } = await api.post('/auth/login', { username, senha });
    localStorage.setItem(TOKEN_KEY, data.token);
    const payload = decodificar(data.token);
    setUser(payload);
    return payload;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_ADMIN_KEY);
    setUser(null);
  }, []);

  // "Ver como": o admin abre o portal com a visão de um usuário de fornecedor (somente
  // leitura, 1h). Guarda a sessão dele e troca pela de visualização emitida pelo servidor.
  const verComo = useCallback(async (usuarioId) => {
    const { data } = await api.post(`/auth/ver-como/${usuarioId}`);
    localStorage.setItem(TOKEN_ADMIN_KEY, localStorage.getItem(TOKEN_KEY));
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(decodificar(data.token));
  }, []);
  const voltarDoVerComo = useCallback(() => {
    const admin = localStorage.getItem(TOKEN_ADMIN_KEY);
    localStorage.removeItem(TOKEN_ADMIN_KEY);
    if (admin && decodificar(admin)) {
      localStorage.setItem(TOKEN_KEY, admin);
      setUser(decodificar(admin));
    } else {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    }
  }, []);

  // Aplica um token recém-emitido (ex.: após trocar a senha provisória)
  const aplicarToken = useCallback((token) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(decodificar(token));
  }, []);

  // Permissão só para admin interno; fornecedor tem acesso fixo aos módulos dele
  const temPermissao = useCallback(
    (nome) => user?.permissoes?.includes(nome) ?? false,
    [user]
  );

  const ehFornecedor = user?.tipo === 'fornecedor';
  const ehInterno = user?.tipo === 'interno';

  return (
    <AuthContext.Provider
      value={{ user, login, logout, aplicarToken, temPermissao, ehFornecedor, ehInterno, apiUrl: API_URL,
        verComo, voltarDoVerComo, visualizando: !!user?.visualizando_como }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
